import { useUiLanguage } from "@/i18n/useUiLanguage";
import { tr } from "@/i18n/copy";
import { createContext, useContext, useEffect, useId, useRef, useState } from 'react';
import { Lightbulb } from 'lucide-react';
import { WORKFLOW_GUIDES } from '@/workflowGuides';

const GuideScope = createContext('');
const dismissed = new Set();
const completedEvent = 'tps:guide-completed';
const readDismissed = key => {
  if (!key) return false;
  try { return window.localStorage.getItem(key) === 'done' || dismissed.has(key); }
  catch { return dismissed.has(key); }
};

export function WorkflowGuideScope({ userId, children }) {
  useUiLanguage();
  return <GuideScope.Provider value={userId ? `user:${userId}` : ''}>{children}</GuideScope.Provider>;
}

export function useWorkflowGuide(feature, fallbackScope = '') {
  const inheritedScope = useContext(GuideScope);
  const scope = inheritedScope || fallbackScope;
  const key = scope ? `tps:guide:${feature}:v1:${scope}` : '';
  const stateKey = key || `unscoped:${feature}`;
  const [state, setState] = useState(() => ({ key: stateKey, open: !readDismissed(key) }));
  const visible = state.key === stateKey ? state.open : !readDismissed(key);
  const id = useId(), buttonRef = useRef(null);
  useEffect(() => {
    setState({ key: stateKey, open: !readDismissed(key) });
    const completed = event => { if (key && event.detail?.key === key) setState({ key: stateKey, open: false }); };
    window.addEventListener(completedEvent, completed);
    return () => window.removeEventListener(completedEvent, completed);
  }, [key, stateKey]);
  // Call only after the operation's successful response, never on a tap,
  // preview, background refresh, or a request which is still pending.
  const complete = () => {
    if (key) {
      dismissed.add(key);
      try { window.localStorage.setItem(key, 'done'); } catch { /* Optional browser persistence. */ }
      window.dispatchEvent(new window.CustomEvent(completedEvent, { detail: { key } }));
    }
    setState(current => current.key === stateKey ? { key: stateKey, open: false } : current);
  };
  return { feature, id, buttonRef, visible, complete, dismiss: complete,
    toggle: () => visible ? complete() : setState({ key: stateKey, open: true }),
    acknowledge: () => { complete(); buttonRef.current?.focus(); },
  };
}

export default function WorkflowGuide({ guide, current, className = '' }) {
  useUiLanguage();
  const definition = WORKFLOW_GUIDES[guide.feature];
  if (!definition) return null;
  return <aside className={`${guide.visible ? 'rounded-xl border border-primary/20 bg-primary/5 p-3' : ''} ${className}`} data-testid={`workflow-guide-${guide.feature}`} aria-label={tr(definition.title)}>
    <div className="flex items-center justify-end gap-2">
      {guide.visible && <><Lightbulb aria-hidden="true" className="h-4 w-4 shrink-0 text-primary"/><p className="min-w-0 flex-1 text-sm font-medium">{tr(definition.title)}</p></>}
      <button ref={guide.buttonRef} type="button" aria-label={tr("Aiuto: {{0}}", {"0": tr(definition.title)})} aria-expanded={guide.visible} aria-controls={guide.id} onClick={guide.toggle}
        className="grid h-11 w-11 shrink-0 place-items-center rounded-full text-muted-foreground hover:bg-primary/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary">
        <span aria-hidden="true" className="grid h-6 w-6 place-items-center rounded-full border text-sm font-semibold">?</span>
      </button>
    </div>
    <div id={guide.id} hidden={!guide.visible}>
      {guide.visible && <>
        {definition.steps.length > 0 && <ol aria-label={tr("Passaggi dell’operazione")} className="mb-2 flex flex-wrap gap-2 text-xs">
          {definition.steps.map((step, index) => <li key={step} aria-current={current === index ? 'step' : undefined} className={`flex items-center gap-1.5 rounded-full border px-2 py-1 ${current === index ? 'border-primary font-semibold text-primary' : 'border-border text-muted-foreground'}`}><span aria-hidden="true" className="font-semibold">{index + 1}</span>{tr(step)}</li>)}
        </ol>}
        <p className="text-sm text-muted-foreground">{tr(definition.text)}</p>
        <button type="button" onClick={guide.acknowledge} className="mt-1 min-h-11 text-sm font-semibold text-primary underline-offset-4 hover:underline">{tr("Ho capito")}</button>
      </>}
    </div>
  </aside>;
}
